<template>
  <div class="min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-primary selection:text-white">
    <Head :title="activeTopicData.title + ' - Documentation'" />

    <!-- Top Navigation Bar -->
    <header class="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/80 backdrop-blur">
      <div class="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
        <div class="flex items-center space-x-8">
          <!-- Logo -->
          <div class="flex items-center space-x-2 cursor-pointer" @click="goToDashboard">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-8 h-8 text-indigo-600">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-3-12v.75m0 3v.75m0 3v.75m0 3V18m-3-12v.75m0 3v.75m0 3v.75m0 3V18" />
            </svg>
            <span class="text-xl font-bold tracking-tight text-slate-900">Gestion de plaintes</span>
          </div>

          <!-- Nav Items -->
          <nav class="hidden md:flex items-center space-x-1">
            <a href="#" class="px-3 py-2 text-sm font-semibold rounded-md text-slate-900 bg-slate-100">Documentation</a>
            <a href="#" class="px-3 py-2 text-sm font-medium rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors">Guides</a>
            <a href="#" class="px-3 py-2 text-sm font-medium rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors">Changelog</a>
            <a href="#" class="px-3 py-2 text-sm font-medium rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors">Centre d'aide</a>
          </nav>
        </div>

        <!-- Search and Auth Actions -->
        <div class="flex items-center space-x-4">
          <div class="relative w-64 hidden sm:block">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4 text-slate-400">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.602 10.602z" />
              </svg>
            </span>
            <input type="text" placeholder="Rechercher ou accéder à.." class="w-full pl-9 pr-4 py-1.5 text-sm bg-slate-100 border-none rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white transition-all" />
            <kbd class="absolute right-2 top-2 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-white border border-slate-200 rounded shadow-sm">/</kbd>
          </div>

          <Link v-if="$page.props.auth && $page.props.auth.user" :href="route('dashboard')" class="px-4 py-1.5 text-sm font-semibold text-white bg-primary hover:bg-hover rounded-lg shadow-sm transition-colors">
            Tableau de bord
          </Link>
          <Link v-else :href="route('login')" class="px-4 py-1.5 text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg shadow-sm transition-colors">
            Connexion
          </Link>

          <!-- Language Selector -->
          <button class="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-.778.099-1.533.284-2.253m0 0L21 12" />
            </svg>
          </button>
        </div>
      </div>
    </header>

    <!-- Main Container -->
    <div class="max-w-[1400px] mx-auto px-6 pb-8 pt-24 flex flex-col lg:flex-row gap-8">
      
      <!-- Left Sidebar: Menu -->
      <aside class="w-full lg:w-64 shrink-0">
        <!-- Platform Selector Dropdown -->
        <div class="relative mb-6">
          <select class="w-full pl-3 pr-10 py-2 text-sm font-semibold bg-white border border-slate-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500/20">
            <option>Plateforme</option>
            <option>Paramètres API</option>
            <option>Intégrations</option>
          </select>
          <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </div>
        </div>

        <!-- Navigation items list -->
        <ul class="space-y-1.5">
          <li v-for="topic in topics" :key="topic.id">
            <button 
              @click="activeTopic = topic.id" 
              class="w-full text-left px-3 py-2 text-sm font-medium rounded-lg flex items-center justify-between transition-all group"
              :class="activeTopic === topic.id ? 'bg-indigo-50 text-indigo-700 font-semibold shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'"
            >
              <div class="flex items-center space-x-2">
                <!-- Dot indicator for active -->
                <span class="w-1.5 h-1.5 rounded-full shrink-0 transition-all" :class="activeTopic === topic.id ? 'bg-indigo-600 scale-100' : 'bg-transparent scale-0 group-hover:scale-100 group-hover:bg-slate-300'"></span>
                <span class="truncate">{{ topic.title }}</span>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-3.5 h-3.5 transition-transform" :class="activeTopic === topic.id ? 'text-indigo-600 translate-x-0.5' : 'text-slate-400 group-hover:translate-x-0.5'">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </li>
        </ul>
      </aside>

      <!-- Center: Main Documentation Content -->
      <main class="flex-1 bg-white rounded-2xl border border-slate-200/80 p-8 shadow-sm">
        <!-- Breadcrumbs -->
        <nav class="flex items-center space-x-2 text-xs font-semibold text-slate-500 mb-4">
          <span>Plateforme</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-3.5 h-3.5 text-slate-400">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
          <span class="text-slate-900">{{ activeTopicData.title }}</span>
        </nav>

        <!-- Meta Header Info -->
        <div class="flex flex-wrap items-center justify-between gap-4 mb-6 border-b border-slate-100 pb-6">
          <div class="flex items-center space-x-2">
            <span class="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
            <span class="text-xs font-bold uppercase tracking-wider text-blue-600">Guide</span>
          </div>
          <div class="flex items-center space-x-3 text-xs text-slate-400 font-medium">
            <span class="flex items-center space-x-1">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
              <span>{{ activeTopicData.readTime }} de lecture</span>
            </span>
          </div>
        </div>

        <!-- Document Content -->
        <article class="prose prose-slate max-w-none">
          <h1 class="text-3xl font-extrabold tracking-tight text-slate-900 mb-6">{{ activeTopicData.title }}</h1>
          
          <div class="space-y-8 text-slate-600 leading-relaxed">
            <!-- Render sections -->
            <div v-for="(section, sIdx) in activeTopicData.sections" :key="sIdx" :id="section.id" class="scroll-mt-20">
              <h2 class="text-xl font-bold text-slate-900 mb-3 border-b border-slate-100 pb-2">{{ section.title }}</h2>
              
              <!-- Content block -->
              <div v-if="section.content" class="mb-4 text-base" v-html="formatContent(section.content)"></div>

              <!-- List items -->
              <ul v-if="section.bullets" class="list-disc pl-5 mb-4 space-y-1">
                <li v-for="(bullet, bIdx) in section.bullets" :key="bIdx" v-html="formatContent(bullet)"></li>
              </ul>

              <!-- Subsections -->
              <div v-if="section.subsections" class="space-y-4 my-4">
                <div v-for="(sub, subIdx) in section.subsections" :key="subIdx" class="pl-4 border-l-2 border-indigo-500">
                  <h3 class="font-bold text-slate-900 mb-1">{{ sub.title }}</h3>
                  <p class="text-sm text-slate-600 mb-2" v-html="formatContent(sub.content)"></p>
                  <ul v-if="sub.bullets" class="list-disc pl-5 mb-2 text-sm space-y-1">
                    <li v-for="(b, bIdx) in sub.bullets" :key="bIdx" v-html="formatContent(b)"></li>
                  </ul>
                </div>
              </div>

              <!-- Actual Image if imageUrl is set, otherwise show Placeholder Card -->
              <div v-if="section.imageUrl" class="my-6">
                <img :src="section.imageUrl" class="w-full h-auto rounded-xl border border-slate-200 shadow-sm" alt="Illustration" />
              </div>
              <div v-else-if="section.hasImage" class="my-6 p-8 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50 flex flex-col items-center justify-center text-slate-400">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-8 h-8 mb-2 text-slate-300">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
                <span class="text-xs font-semibold uppercase tracking-wider text-slate-400">Emplacement d'image à ajouter</span>
              </div>

            </div>
          </div>
        </article>
      </main>

      <!-- Right Sidebar: Table of Contents -->
      <aside class="w-full lg:w-56 shrink-0 lg:sticky lg:top-24 h-fit">
        <h3 class="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Sommaire</h3>
        <nav>
          <ul class="space-y-3 border-l border-slate-200 pl-4 font-medium text-sm">
            <li v-for="section in activeTopicData.sections" :key="section.id">
              <a 
                :href="'#' + section.id"
                class="block hover:text-slate-950 transition-colors py-0.5"
                :class="activeSection === section.id ? 'text-indigo-600 font-semibold' : 'text-slate-500'"
              >
                {{ section.title }}
              </a>
            </li>
          </ul>
        </nav>
      </aside>

    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'

export default {
  components: {
    Head,
    Link,
  },
  data() {
    return {
      activeTopic: 'dashboard',
      activeSection: 'overview',
      topics: [
        { id: 'dashboard', title: '1. Tableaux de Bord & Rapports' },
        { id: 'tickets', title: '2. Gestion des Tickets' },
        { id: 'contacts', title: '3. Gestion des Contacts' },
        { id: 'admin', title: '4. Utilisateurs et Rôles' },
        { id: 'phone', title: '5. Intégration Téléphonique & IVR' },
        { id: 'settings', title: '6. Paramètres & Personnalisation' },
        { id: 'referential', title: '7. Gestion des Référentiels' },
        { id: 'activity', title: '8. Journal d\'Activités' },
      ],
      documentationData: {
        'dashboard': {
          title: 'Tableaux de Bord et Rapports',
          readTime: '2 min',
          sections: [
            {
              id: 'overview',
              title: 'Aperçu global',
              content: 'Le tableau de bord offre une vue globale de l\'activité de la plateforme. Il permet notamment de suivre le nombre de nouveaux tickets, ouverts, résolus, fermés et non attribués.'
            },
            {
              id: 'realtime-stats',
              title: 'Statistiques en temps réel',
              content: 'Les indicateurs sont mis à jour automatiquement afin de fournir une vision instantanée de l\'activité.'
            },
            {
              id: 'performance-charts',
              title: 'Graphiques de performance',
              content: 'Plusieurs graphiques permettent d\'analyser les performances des équipes, notamment le délai moyen de première réponse, le temps moyen de résolution et la répartition des tickets par statut.',
              hasImage: true,
              imageUrl: '/images/docs/performance-charts.png'
            },
            {
              id: 'data-export',
              title: 'Export des données',
              content: 'Les données peuvent être exportées au format **Excel (.xlsx)** afin de réaliser des analyses complémentaires ou produire des rapports.'
            }
          ]
        },
        'tickets': {
          title: 'Gestion des Tickets',
          readTime: '3 min',
          sections: [
            {
              id: 'overview',
              title: 'Introduction',
              content: 'Le module **Tickets** constitue le cœur de la plateforme. Il permet d\'enregistrer, suivre et traiter toutes les réclamations ou plaintes des clients jusqu\'à leur résolution.'
            },
            {
              id: 'accessing-tickets',
              title: 'Accéder aux tickets',
              content: 'Depuis le menu **Tickets**, vous accédez à la liste complète des réclamations enregistrées. Chaque ticket affiche les informations suivantes :',
              bullets: [
                'Identifiant (code unique du ticket)',
                'Objet de la réclamation',
                'Département concerné',
                'Statut d\'évolution',
                'Date de création'
              ],
              imageUrl: '/images/docs/accessing-tickets.png',
              hasImage: true
            },
            {
              id: 'creating-a-ticket',
              title: 'Création d\'un ticket',
              content: 'Pour créer une nouvelle réclamation, cliquez sur le bouton **Nouveau ticket**. La création peut se faire par deux canaux :',
              subsections: [
                {
                  title: 'Canal téléphonique (IVR)',
                  content: 'Lorsqu\'un client contacte le centre d\'appels via le serveur vocal interactif (IVR), un ticket peut être créé automatiquement à partir de l\'appel.'
                },
                {
                  title: 'Canal standard',
                  content: 'Les agents peuvent créer manuellement une réclamation depuis le bouton **Nouveau ticket**. Lors de la création, les informations suivantes peuvent être renseignées :',
                  bullets: [
                    'Client concerné',
                    'Objet de la réclamation',
                    'Description détaillée',
                    'Département responsable',
                    'Catégorie',
                    'Type',
                    'Priorité',
                    'Statut initial',
                    'Agent responsable (si applicable)'
                  ]
                }
              ],
              imageUrl: '/images/docs/create-tickets.png',
              hasImage: true
            },
            {
              id: 'lifecycle',
              title: 'Suivi du cycle de vie',
              content: 'Chaque ticket évolue au cours de son traitement selon différents statuts :',
              bullets: [
                'Nouveau',
                'Ouvert',
                'En attente',
                'Résolu',
                'Fermé'
              ],
              imageUrl: '/images/docs/lifecycle.png',
              hasImage: true
            },
            {
              id: 'assignment',
              title: 'Attribution des tickets',
              content: 'Les tickets peuvent être attribués automatiquement selon les règles définies par l\'administrateur ou attribués manuellement à un agent ou à un département.'
            },
            {
              id: 'classification',
              title: 'Classification des tickets',
              content: 'Afin de faciliter leur traitement, chaque ticket peut être classé selon plusieurs critères :',
              subsections: [
                { title: 'Département', content: 'Correspond au service responsable du traitement (ex: Service Technique, Facturation, Service Client, Maintenance).' },
                { title: 'Catégorie', content: 'Permet de regrouper les tickets par domaine fonctionnel.' },
                { title: 'Type', content: 'Détermine la nature de la demande (ex: Réclamation, Demande d\'information, Incident, Suggestion).' },
                { title: 'Priorité', content: 'Définit le niveau d\'urgence (ex: Faible, Moyenne, Élevée, Critique).' },
                { title: 'Statut', content: 'Indique l\'état actuel du ticket tout au long de son traitement.' }
              ]
            },
            {
              id: 'notes-comments',
              title: 'Notes et commentaires',
              content: 'Chaque ticket dispose d\'un espace de discussion permettant aux agents d\'échanger avec le client. Deux modes de communication sont disponibles :',
              bullets: [
                '**Message texte** : le client reçoit les réponses sous forme de SMS.',
                '**Message vocal** : l\'agent peut envoyer un enregistrement audio qui sera diffusé au client lors d\'un appel téléphonique automatisé.'
              ],
              imageUrl: '/images/docs/notes-comments.png',
              hasImage: true
            }
          ]
        },
        'contacts': {
          title: 'Gestion des Contacts',
          readTime: '2 min',
          sections: [
            {
              id: 'overview',
              title: 'Introduction',
              content: 'Le module **Contacts** centralise les informations relatives aux personnes ayant effectué une réclamation. Depuis le menu **Contacts**, vous pouvez consulter l\'ensemble des contacts enregistrés. Chaque fiche contient notamment :',
              bullets: [
                'Nom et prénom',
                'Numéro de téléphone',
                'Adresse e-mail',
                'Département ou service associé'
              ],
              imageUrl: '/images/docs/contacts-overview.png',
              hasImage: true
            },
            {
              id: 'create-contact',
              title: 'Création d\'un contact',
              content: 'Cliquez sur **Nouveau contact** pour enregistrer un nouveau contact dans le système.'
            },
            {
              id: 'edit-contact',
              title: 'Consultation et modification',
              content: 'En sélectionnant un contact, vous accédez à sa fiche détaillée où vous pouvez consulter ses informations, modifier ses coordonnées et mettre à jour les données enregistrées.',
              imageUrl: '/images/docs/edit-contact.png',
              hasImage: true
            }
          ]
        },
        'admin': {
          title: 'Administration des Utilisateurs et des Rôles',
          readTime: '3 min',
          sections: [
            {
              id: 'overview',
              title: 'Introduction',
              content: 'Ce module réservé aux administrateurs permet de gérer les utilisateurs de la plateforme ainsi que leurs droits d\'accès.'
            },
            {
              id: 'user-management',
              title: 'Gestion des utilisateurs',
              content: 'Les administrateurs peuvent créer un utilisateur, modifier ses informations, désactiver/supprimer un compte et affecter un utilisateur à un ou plusieurs départements.'
            },
            {
              id: 'role-management',
              title: 'Gestion des rôles',
              content: 'Les rôles permettent de définir précisément les autorisations accordées aux utilisateurs (consultation, création, modification, suppression). Ces droits sont configurables pour chaque module.',
              subsections: [
                { title: 'Administrateur système', content: 'Dispose d\'un accès complet à l\'ensemble des fonctionnalités.' },
                { title: 'Administrateur local', content: 'Dispose uniquement des droits attribués à son organisation ou à son périmètre de gestion.' }
              ],
              imageUrl: '/images/docs/role-management.png',
              hasImage: true
            }
          ]
        },
        'phone': {
          title: 'Intégration Téléphonique et IVR',
          readTime: '2 min',
          sections: [
            {
              id: 'overview',
              title: 'Journal des appels',
              content: 'La plateforme intègre un système téléphonique permettant de suivre les interactions. Le module **Calls** répertorie l\'ensemble des appels entrants et sortants avec leur durée, date, sens de l\'appel et statut.',
              imageUrl: '/images/docs/call-logs.png',
              hasImage: true
            },
            {
              id: 'sms-logs',
              title: 'Journal des SMS',
              content: 'Le module **SMS** permet de consulter tous les messages envoyés et reçus afin de faciliter le suivi des échanges.'
            }
          ]
        },
        'settings': {
          title: 'Paramètres et Personnalisation',
          readTime: '3 min',
          sections: [
            {
              id: 'general-settings',
              title: 'Paramètres généraux',
              content: 'Les administrateurs peuvent personnaliser l\'identité visuelle de l\'application (nom de la plateforme, logo clair, logo sombre, favicon) ainsi que la couleur principale, la couleur secondaire (mode sombre) et la couleur de survol.',
              imageUrl: '/images/docs/general-settings.png',
              hasImage: true
            },
            {
              id: 'email-templates',
              title: 'Modèles d\'e-mails',
              content: 'Les notifications envoyées par la plateforme utilisent des modèles personnalisables via un éditeur HTML.',
              hasImage: false
            },
            {
              id: 'smtp-config',
              title: 'Configuration SMTP',
              content: 'Configurez les paramètres du serveur d\'envoi pour l\'expédition des courriers électroniques.'
            }
          ]
        },
        'referential': {
          title: 'Gestion des Référentiels',
          readTime: '2 min',
          sections: [
            {
              id: 'overview',
              title: 'Départements et Catégories',
              content: 'Les référentiels permettent de configurer les éléments utilisés lors de la création des tickets (Départements, Catégories, Types, Priorités).'
            },
            {
              id: 'statuses-config',
              title: 'Statuts personnalisés',
              content: 'Permet de personnaliser les différentes étapes du cycle de vie d\'un ticket.',
              imageUrl: '/images/docs/statuses-config.png',
              hasImage: true
            }
          ]
        },
        'activity': {
          title: 'Journal d\'Activités',
          readTime: '2 min',
          sections: [
            {
              id: 'overview',
              title: 'Traçabilité des opérations',
              content: 'Le **Journal d\'activités** permet de tracer toutes les actions réalisées sur la plateforme (connexions, modifications, créations, suppressions, changements de statut). C\'est un outil essentiel pour la sécurité et l\'audit.',
              imageUrl: '/images/docs/activity-log.png',
              hasImage: true
            }
          ]
        }
      }
    }
  },
  computed: {
    activeTopicData() {
      return this.documentationData[this.activeTopic] || this.documentationData['dashboard']
    }
  },
  methods: {
    goToDashboard() {
      if (this.$page.props.auth?.user) {
        this.$inertia.visit(this.route('dashboard'))
      } else {
        this.$inertia.visit('/')
      }
    },
    formatContent(content) {
      if (!content) return ''
      // Replace markdown bold **text** with HTML font-bold
      return content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-slate-900">$1</strong>')
    }
  },
  mounted() {
    // Basic Intersection Observer to highlight active TOC section
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -40% 0px',
      threshold: 0.1
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.activeSection = entry.target.id
        }
      })
    }, observerOptions)

    // Observe active sections
    this.$nextTick(() => {
      this.activeTopicData.sections.forEach((section) => {
        const el = document.getElementById(section.id)
        if (el) observer.observe(el)
      })
    })

    // Re-bind when topic changes
    this.$watch('activeTopic', () => {
      this.$nextTick(() => {
        this.activeTopicData.sections.forEach((section) => {
          const el = document.getElementById(section.id)
          if (el) observer.observe(el)
        })
      })
    })
  }
}
</script>

<style scoped>
.bg-dark {
  background-color: #1a1a1a;
}
</style>
